import React from "react";
import { Form, TimePicker } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import duration from "dayjs/plugin/duration";
import {
  validateNoComponentsOverlap,
  analyzeBreaksInDuration,
} from "../../utilities/shift";
import get from "lodash/get";

dayjs.extend(isBetween);
dayjs.extend(duration);

const MinMaxTimePicker = ({ minTime, maxTime, name, onChange, form }) => {
  const getDisabledTimes = (currentMinTime, currentMaxTime) => {
    const baseDay = dayjs().startOf("day");
    const normalizedMinTime = baseDay
      .hour(currentMinTime.hour())
      .minute(currentMinTime.minute())
      .second(currentMinTime.second());

    let normalizedMaxTime = baseDay
      .hour(currentMaxTime.hour())
      .minute(currentMaxTime.minute())
      .second(currentMaxTime.second());

    while (normalizedMaxTime.isBefore(normalizedMinTime)) {
      normalizedMaxTime = normalizedMaxTime.add(1, "day");
    }

    return {
      disabledHours: () => {
        const hours = [];
        for (let i = 0; i < 24; i++) {
          const checkTime = baseDay.hour(i).minute(0).second(0);
          if (
            !checkTime.isBetween(
              normalizedMinTime,
              normalizedMaxTime,
              "hour",
              "[]"
            ) &&
            !checkTime
              .add(1, "day")
              .isBetween(normalizedMinTime, normalizedMaxTime, "hour", "[]") // Check for the next day
          ) {
            hours.push(i);
          }
        }
        return hours;
      },

      disabledMinutes: (selectedHour) => {
        const minutes = [];
        for (let i = 0; i < 60; i++) {
          const checkTime = baseDay.hour(selectedHour).minute(i).second(0);
          if (
            !checkTime.isBetween(
              normalizedMinTime,
              normalizedMaxTime,
              "minute",
              "[]"
            ) &&
            !checkTime
              .add(1, "day")
              .isBetween(normalizedMinTime, normalizedMaxTime, "minute", "[]") // Check for the next day
          ) {
            minutes.push(i);
          }
        }
        return minutes;
      },

      disabledSeconds: (selectedHour, selectedMinute) => {
        const seconds = [];
        for (let i = 0; i < 60; i++) {
          const checkTime = baseDay
            .hour(selectedHour)
            .minute(selectedMinute)
            .second(i);
          if (
            !checkTime.isBetween(
              normalizedMinTime,
              normalizedMaxTime,
              "second",
              "[]"
            ) &&
            !checkTime
              .add(1, "day")
              .isBetween(normalizedMinTime, normalizedMaxTime, "second", "[]")
          ) {
            seconds.push(i);
          }
        }
        return seconds;
      },
    };
  };

  const { disabledHours, disabledMinutes, disabledSeconds } = getDisabledTimes(
    minTime,
    maxTime
  );

  return (
    <Form.Item
      name={[name, "start_time"]}
      label="Start Time"
      rules={[
        { required: true, message: "Start time is required" },
        validateNoComponentsOverlap("components", name, minTime,maxTime),
      ]}
    >
      <TimePicker
        className="w-full"
        placeholder="Start Time"
        size="small"
        format="HH:mm"
        minuteStep={15}
        disabledHours={disabledHours}
        disabledMinutes={disabledMinutes}
        disabledSeconds={disabledSeconds}
        hideDisabledOptions={true}
        onChange={(newTimeValue) => {
          const updatedValues = form.getFieldValue(["components", name]);
          updatedValues.start_time = newTimeValue;
          onChange(name, updatedValues);
        }}
      />
    </Form.Item>
  );
};

export default MinMaxTimePicker;

const MinMaxEndTimePicker = ({
  minTime,
  maxTime,
  name,
  form,
  filter,
  onChange,
}) => {
  const getDisabledTimes = (currentMinTime, currentMaxTime) => {
    const baseDay = dayjs().startOf("day");
    const normalizedMinTime = baseDay
      .hour(currentMinTime.hour())
      .minute(currentMinTime.minute())
      .second(currentMinTime.second());

    let normalizedMaxTime = baseDay
      .hour(currentMaxTime.hour())
      .minute(currentMaxTime.minute())
      .second(currentMaxTime.second());

    while (normalizedMaxTime.isBefore(normalizedMinTime)) {
      normalizedMaxTime = normalizedMaxTime.add(1, "day");
    }

    return {
      disabledHours: () => {
        const hours = [];
        for (let i = 0; i < 24; i++) {
          const checkTime = baseDay.hour(i).minute(0).second(0);
          if (
            !checkTime.isBetween(
              normalizedMinTime,
              normalizedMaxTime,
              "hour",
              "[]"
            ) &&
            !checkTime
              .add(1, "day")
              .isBetween(normalizedMinTime, normalizedMaxTime, "hour", "[]")
          ) {
            hours.push(i);
          }
        }
        return hours;
      },

      disabledMinutes: (selectedHour) => {
        const minutes = [];
        for (let i = 0; i < 60; i++) {
          const checkTime = baseDay.hour(selectedHour).minute(i).second(0);
          if (
            !checkTime.isBetween(
              normalizedMinTime,
              normalizedMaxTime,
              "minute",
              "[]"
            ) &&
            !checkTime
              .add(1, "day")
              .isBetween(normalizedMinTime, normalizedMaxTime, "minute", "[]")
          ) {
            minutes.push(i);
          }
        }
        return minutes;
      },

      disabledSeconds: (selectedHour, selectedMinute) => {
        const seconds = [];
        for (let i = 0; i < 60; i++) {
          const checkTime = baseDay
            .hour(selectedHour)
            .minute(selectedMinute)
            .second(i);
          if (
            !checkTime.isBetween(
              normalizedMinTime,
              normalizedMaxTime,
              "second",
              "[]"
            ) &&
            !checkTime
              .add(1, "day")
              .isBetween(normalizedMinTime, normalizedMaxTime, "second", "[]")
          ) {
            seconds.push(i);
          }
        }
        return seconds;
      },
    };
  };

  const { disabledHours, disabledMinutes, disabledSeconds } = getDisabledTimes(
    minTime,
    maxTime
  );

  return (
    <Form.Item
      name={[name, "end_time"]}
      label="End Time"
      rules={[
        {
          required: true,
          message: "End time is required",
        },
        validateNoComponentsOverlap("components", name,minTime,maxTime),
      ]}
    >
      <TimePicker
        className="w-full"
        placeholder="End Time"
        size="small"
        format="HH:mm"
        minuteStep={15}
        disabledHours={disabledHours}
        disabledMinutes={disabledMinutes}
        disabledSeconds={disabledSeconds}
        hideDisabledOptions={true}
        onChange={(value) => {
          const startVal = form.getFieldValue([
            "components",
            name,
            "start_time",
          ]);
          const endVal = form.getFieldValue(["components", name, "end_time"]);

          const duration = {
            start_time: startVal?.format ? startVal.format("HH:mm") : startVal,
            end_time: endVal?.format ? endVal.format("HH:mm") : endVal,
          };
          const { totalOverlappingBreakTimeMinutes } = analyzeBreaksInDuration(
            get(filter, "company.break_timings", []),
            duration
          );
          form.setFieldValue(
            ["components", name, "planned_down_time"],
            totalOverlappingBreakTimeMinutes
          );

          const updatedValues = form.getFieldValue(["components", name]);
          updatedValues.end_time = value;
          onChange(name, updatedValues);
        }}
      />
    </Form.Item>
  );
};

export { MinMaxEndTimePicker };
